import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";

import "./BlogDetails.css";

import { FaFacebookF } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { FaPinterest } from "react-icons/fa";
import { FaPlus } from "react-icons/fa6";
import { GoChevronLeft } from "react-icons/go";
import { GoChevronRight } from "react-icons/go";
import { getBlogPosts, getBlogPost } from "../../../api/storefront";
import { getImageUrl } from "../../../api/client";
import Seo from "../../Seo/Seo";
import { useSiteSeo } from "../../../context/SiteSeoContext";
import { absoluteCanonicalUrl, toAbsoluteOgImage } from "../../../utils/siteUrl";
import { stripHtmlToText, truncateMetaDescription } from "../../../utils/seoText";
import { usePrerenderReady } from "../../../utils/prerenderReady";

const BlogDetails = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [adjacent, setAdjacent] = useState({ prev: null, next: null });
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const siteCtx = useSiteSeo();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const s = String(slug || "").trim();
      if (!s) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setLoading(true);
      setNotFound(false);
      const p = await getBlogPost(decodeURIComponent(s));
      if (cancelled) return;
      if (!p || !p.slug) {
        setPost(null);
        setNotFound(true);
        setLoading(false);
        return;
      }
      setPost(p);

      try {
        const list = await getBlogPosts({ limit: 200 });
        const arr = Array.isArray(list) ? list : [];
        const idx = arr.findIndex((row) => row.slug === p.slug);
        setAdjacent({
          prev: idx > 0 ? arr[idx - 1] : null,
          next: idx >= 0 && idx < arr.length - 1 ? arr[idx + 1] : null,
        });
      } catch (_) {
        setAdjacent({ prev: null, next: null });
      }
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  usePrerenderReady(!loading && (Boolean(post) || notFound));

  const titleSuffix =
    siteCtx.organization_name && String(siteCtx.organization_name).trim()
      ? ` | ${String(siteCtx.organization_name).trim()}`
      : " | Gem Fashion Studios";

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const canonicalSlug = post?.slug ? encodeURIComponent(post.slug) : String(slug || "").trim();

  const shareUrl =
    canonicalSlug !== ""
      ? absoluteCanonicalUrl(`/blog/${canonicalSlug}`)
      : typeof window !== "undefined"
        ? `${window.location.origin}/blog`
        : "";

  if (loading) {
    return (
      <section className="blogDetailsSection" aria-busy="true">
        <p style={{ padding: "3rem", textAlign: "center" }}>Loading article…</p>
      </section>
    );
  }

  if (notFound || !post) {
    return (
      <section className="blogDetailsSection">
        <Seo
          title={`Post not found${titleSuffix}`}
          description="The blog article you requested is unavailable."
          canonicalUrl={
            slug ? absoluteCanonicalUrl(`/blog/${encodeURIComponent(String(slug).trim())}`) : absoluteCanonicalUrl("/blog")
          }
          noindex
        />
        <div style={{ padding: "3rem", textAlign: "center" }}>
          <p>Post not found.</p>
          <Link to="/blog">Back to blog</Link>
        </div>
      </section>
    );
  }

  const dateDisplay = post.published_at
    ? new Date(post.published_at).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : post.created_at
      ? new Date(post.created_at).toLocaleDateString(undefined, {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : "";

  const excerptText = truncateMetaDescription(
    stripHtmlToText(post.excerpt || "") || stripHtmlToText(post.body_html || "").slice(0, 400)
  );
  const metaTitle = `${String(post.title).trim()}${titleSuffix}`;
  const ogImgRaw = post.featured_image_url ? getImageUrl(post.featured_image_url) : "";
  const ogResolved = ogImgRaw ? toAbsoluteOgImage(ogImgRaw, getImageUrl) : "";

  const publishedIso =
    post.published_at ||
    post.created_at ||
    new Date().toISOString();
  const modIso = post.created_at || post.published_at || publishedIso;

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: excerptText,
    datePublished: new Date(publishedIso).toISOString(),
    dateModified: new Date(modIso).toISOString(),
    author: {
      "@type": "Person",
      name: post.author_name || "Editor",
    },
    image: ogResolved ? [ogResolved] : undefined,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": shareUrl,
    },
    publisher: {
      "@type": "Organization",
      name: siteCtx.organization_name || "Gem Fashion Studios",
      ...(siteCtx.organization_logo_url ? { logo: siteCtx.organization_logo_url } : {}),
    },
  };

  return (
    <>
      <Seo
        title={metaTitle}
        description={excerptText}
        canonicalUrl={shareUrl}
        ogImage={ogResolved || undefined}
        ogImageAlt={post.title ? `${post.title}, cover` : "Blog cover"}
        ogType="article"
        jsonLd={[articleLd]}
      />
      <div className="blogDetailsSection">
        <div className="blogDetailsSectionContainer">
          <div style={{ marginBottom: 12 }}>
            <Link to="/blog" onClick={scrollToTop} style={{ textDecoration: "none", fontWeight: 600 }}>
              ← Blog
            </Link>
          </div>
          <div className="blogDetailsHeading">
            <h2>{post.title}</h2>
            <div className="blogDetailsMetaData">
              <span>by {post.author_name || "editor"}</span>
              <span>{dateDisplay}</span>
              {post.category_label ? <span>{String(post.category_label).toUpperCase()}</span> : null}
            </div>
          </div>
          {post.featured_image_url ? (
            <div className="blogDetailsFeaturedImg">
              <img
                fetchPriority="high"
                src={getImageUrl(post.featured_image_url)}
                alt={post.title ? `${post.title}, cover` : "Blog cover"}
              />
            </div>
          ) : null}
          <div
            className="blogDetailsContent blogDetailsContentHtml"
            dangerouslySetInnerHTML={{ __html: post.body_html || "" }}
          />
          <div className="share-buttons">
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
              className="share-button facebook"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaFacebookF /> Share on Facebook
            </a>
            <a
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}`}
              className="share-button twitter"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaXTwitter />
              Share on Twitter
            </a>
            <a
              href={`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}`}
              className="share-button pinterest"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaPinterest /> Share on Pinterest
            </a>
            <button type="button" className="share-button more" aria-label="More">
              <FaPlus size={20} />
            </button>
          </div>
          <div className="blogDetailsNextPrev">
            <div className="blogDetailsNextPrevContainer">
              {adjacent.prev ? (
                <>
                  <div className="blogDetailsNextPrevContainerIcon" role="presentation" onClick={scrollToTop}>
                    <Link to={`/blog/${encodeURIComponent(adjacent.prev.slug)}`} className="blogDetailsNavInner">
                      <GoChevronLeft size={20} />
                      <p>PREVIOUS POST</p>
                    </Link>
                  </div>
                  <p>{adjacent.prev.title}</p>
                </>
              ) : (
                <>
                  <div className="blogDetailsNextPrevContainerIcon blogDetailsNextPrevContainerIcon--muted">
                    <GoChevronLeft size={20} />
                    <p>PREVIOUS POST</p>
                  </div>
                  <p>—</p>
                </>
              )}
            </div>
            <div className="blogDetailsNextPrevContainer">
              {adjacent.next ? (
                <>
                  <div className="blogDetailsNextPrevContainerIcon2" role="presentation" onClick={scrollToTop}>
                    <Link to={`/blog/${encodeURIComponent(adjacent.next.slug)}`} className="blogDetailsNavInner">
                      <p>NEXT POST</p>
                      <GoChevronRight size={20} />
                    </Link>
                  </div>
                  <p style={{ textAlign: "right" }}>{adjacent.next.title}</p>
                </>
              ) : (
                <>
                  <div className="blogDetailsNextPrevContainerIcon2 blogDetailsNextPrevContainerIcon--muted">
                    <p>NEXT POST</p>
                    <GoChevronRight size={20} />
                  </div>
                  <p style={{ textAlign: "right" }}>—</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogDetails;
